import { ArrowLeft, Moon, Sun } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { GGJLogoFull } from '../Brand/GGJLogo';
import { useTheme } from '../../contexts/ThemeContext';

import PaymentResultCover, { type PaymentResultCoverVariant } from './PaymentResultCover';

interface PaymentPinSplitLayoutProps {
  coverVariant: PaymentResultCoverVariant;
  coverTitle: string;
  coverSubtitle: string;
  onBack: () => void;
  backLabel?: string;
  children: React.ReactNode;
}

/**
 * Split layout shell — mirrors auth login pin-split (hero left, content right).
 */
const PaymentPinSplitLayout: React.FC<PaymentPinSplitLayoutProps> = ({
  coverVariant,
  coverTitle,
  coverSubtitle,
  onBack,
  backLabel,
  children,
}) => {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="payment-pin-split">
      <header className="payment-pin-split__header">
        <GGJLogoFull
          iconSize={26}
          fontSize={18}
          className="payment-pin-split__logo"
        />
        <div className="payment-pin-split__header-actions">
          <button
            type="button"
            onClick={onBack}
            className="payment-pin-split__back-btn"
          >
            <ArrowLeft className="w-4 h-4 shrink-0" aria-hidden />
            <span>
              {backLabel ?? t('views.paymentSuccess.backToCheckout', { defaultValue: 'Back to checkout' })}
            </span>
          </button>
          <button
            type="button"
            onClick={toggleTheme}
            className="payment-pin-split__theme-btn"
            aria-label={theme === 'dark'
              ? t('views.paymentSuccess.switchToLight', { defaultValue: 'Switch to light theme' })
              : t('views.paymentSuccess.switchToDark', { defaultValue: 'Switch to dark theme' })}
          >
            {theme === 'dark' ? (
              <Sun className="w-[1.125rem] h-[1.125rem] text-amber-400" aria-hidden />
            ) : (
              <Moon className="w-[1.125rem] h-[1.125rem]" aria-hidden />
            )}
          </button>
        </div>
      </header>

      <div className="payment-pin-split__body">
        <PaymentResultCover
          variant={coverVariant}
          title={coverTitle}
          subtitle={coverSubtitle}
        />

        <main className="payment-pin-split__panel">
          <div className="payment-pin-split__shell">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default PaymentPinSplitLayout;
